import { Pagination } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';

interface PageListProps {
    totalCount: number;
    itemsPerPage: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    className?: string;
    hideOnEmpty?: boolean;
}

function PageList({ totalCount, itemsPerPage, currentPage, setCurrentPage, className, hideOnEmpty = true }: PageListProps) {
    const isSmall = useMediaQuery({ query: '(max-width: 576px)' });
    const numberOfPages = Math.ceil(totalCount / itemsPerPage);

    if (numberOfPages <= 1 && hideOnEmpty) {
        // Nothing to paginate
        return null;
    }

    // We only need to show the ellipses if:
    // 1. There are more than 9 pages total
    // 2. The current page is more than 5 items from either end
    const showEllipsisStart = (numberOfPages > 9) && (currentPage > 5);
    const showEllipsisEnd = (numberOfPages > 9) && (currentPage < numberOfPages - 4);

    let startingPage, endingPage;
    if (showEllipsisStart && showEllipsisEnd) {
        // Both ellipses shown, range is 5 pages centered on current page
        startingPage = currentPage - 2;
        endingPage = currentPage + 2;
    } else if (showEllipsisStart) {
        // Only start ellipsis shown, range is last 7 pages
        startingPage = numberOfPages - 6;
        endingPage = numberOfPages;
    } else if (showEllipsisEnd) {
        // Only end ellipsis shown, range is first 7 pages
        startingPage = 1;
        endingPage = 7;
    } else {
        // No ellipses shown, display all pages in range
        startingPage = 1;
        endingPage = numberOfPages;
    }
    
    let items=[];

    if (showEllipsisStart) {
        items.push(
            <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>1</Pagination.Item>,
            <Pagination.Ellipsis key="start-ellipsis" disabled />
        );
    }

    for (let number = startingPage; number <= endingPage; number++) {
        items.push(
            <Pagination.Item 
                key={number} 
                active={number === currentPage}
                onClick={() => setCurrentPage(number)}
            >
                {number}
            </Pagination.Item>,
        );
    }

    if (showEllipsisEnd) {
        items.push(
            <Pagination.Ellipsis key="end-ellipsis" disabled />,
            <Pagination.Item key={numberOfPages} onClick={() => setCurrentPage(numberOfPages)}>{numberOfPages}</Pagination.Item>
        );
    }

    return (
        <Pagination size="sm" className={className}>
            <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={isSmall ? { width: '3rem' }: { width: '1.5rem' }}
            />
            <Pagination.Prev
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={isSmall ? { width: '3rem' } : { width: '1.5rem' }}
            />
            {isSmall ? (
                <Pagination.Item
                    active={true}
                    style={{ width: '8rem' }}
                >
                    {`Page ${currentPage} of ${numberOfPages}`}
                </Pagination.Item>
            ) : (
                items
            )}
            <Pagination.Next
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === numberOfPages}
                style={isSmall ? { width: '3rem' } : { width: '1.5rem' }}
            />
            <Pagination.Last
                onClick={() => setCurrentPage(numberOfPages)}
                disabled={currentPage === numberOfPages}
                style={isSmall ? { width: '3rem' } : { width: '1.5rem' }}
            />
        </Pagination>
    );
}

export default PageList;